import { useEffect, useRef, useState, useCallback } from 'react';
import { pricingTrackingService } from '../services/pricingTrackingService';
import { getAffiliateCookie } from '../utils/affiliateCookies';

interface MousePosition {
  x: number;
  y: number;
  timestamp: number;
}

interface ClickEvent {
  x: number;
  y: number;
  timestamp: number;
  target: string;
  isDeadClick: boolean;
}

interface ScrollEvent {
  depth: number;
  speed: number;
  direction: 'down' | 'up';
  timestamp: number;
}

export const usePricingTracking = (userId: string | null) => {
  const sessionIdRef = useRef<string>(
    `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  
  // Capturer les cookies affiliés au chargement de la page
  const affiliateCookieRef = useRef(getAffiliateCookie());
  
  // Log du cookie affilié capturé
  useEffect(() => {
    // console.log supprimé
  }, []);
  
  // Tracking du temps avant premier clic
  const [firstClickTime, setFirstClickTime] = useState<number | null>(null);
  const pageLoadTime = useRef<number>(Date.now());
  
  // Tracking des mouvements de souris
  const mousePositions = useRef<MousePosition[]>([]);
  const lastMouseMove = useRef<number>(Date.now());
  
  // Tracking des clics
  const clicks = useRef<ClickEvent[]>([]);
  const lastClickPosition = useRef<{ x: number; y: number; time: number } | null>(null);
  const rageClickThreshold = 500; // ms entre clics pour détecter rage click
  const rageClickDistance = 50; // pixels de distance max pour rage click
  
  // Tracking du scroll
  const scrollData = useRef<ScrollEvent[]>([]);
  const lastScrollPosition = useRef<number>(0);
  const lastScrollTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  
  // Tracking des hovers sur CTA
  const ctaHovers = useRef<{ element: string; duration: number; timestamp: number }[]>([]);
  const ctaHoverStart = useRef<{ element: string; time: number } | null>(null);
  
  // Tracking des interactions
  const interactions = useRef<{
    toggleClicks: number;
    tooltipOpens: number;
    faqOpens: string[];
    planComparisons: number;
    textCopyAttempts: number;
  }>({
    toggleClicks: 0,
    tooltipOpens: 0,
    faqOpens: [],
    planComparisons: 0,
    textCopyAttempts: 0,
  });

  // Détection d'hésitations (mouvements erratiques)
  const detectHesitation = useCallback(() => {
    if (mousePositions.current.length < 10) return 0;
    
    const recentPositions = mousePositions.current.slice(-10);
    let directionChanges = 0;
    let totalDistance = 0;
    
    for (let i = 1; i < recentPositions.length; i++) {
      const prev = recentPositions[i - 1];
      const curr = recentPositions[i];
      
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      totalDistance += distance;
      
      if (i > 1) {
        const prevPrev = recentPositions[i - 2];
        const prevDx = prev.x - prevPrev.x;
        const prevDy = prev.y - prevPrev.y;
        
        // Détecter changement de direction
        const dotProduct = dx * prevDx + dy * prevDy;
        if (dotProduct < 0) {
          directionChanges++;
        }
      }
    }
    
    // Hésitation = beaucoup de changements de direction sur peu de distance
    const hesitationScore = totalDistance > 0 ? (directionChanges / totalDistance) * 1000 : 0;
    return hesitationScore;
  }, []);

  // Tracking des mouvements de souris
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      mousePositions.current.push({
        x: e.clientX,
        y: e.clientY,
        timestamp: now,
      });
      
      // Garder seulement les 50 dernières positions
      if (mousePositions.current.length > 50) {
        mousePositions.current = mousePositions.current.slice(-50);
      }
      
      lastMouseMove.current = now;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Tracking des clics
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const now = Date.now();
      const target = e.target as HTMLElement;
      
      // Temps avant premier clic
      if (firstClickTime === null) {
        const ttfc = now - pageLoadTime.current;
        setFirstClickTime(ttfc);
      }
      
      // Détecter dead clicks (clics sur éléments non-cliquables)
      const isClickable = target.tagName === 'BUTTON' || 
                         target.tagName === 'A' || 
                         target.tagName === 'INPUT' ||
                         target.onclick !== null ||
                         target.getAttribute('role') === 'button' ||
                         target.style.cursor === 'pointer' ||
                         target.closest('button') !== null ||
                         target.closest('a') !== null;
      
      const clickEvent: ClickEvent = {
        x: e.clientX,
        y: e.clientY,
        timestamp: now,
        target: getElementDescription(target),
        isDeadClick: !isClickable,
      };
      
      clicks.current.push(clickEvent);
      
      // Détecter rage clicks
      if (lastClickPosition.current) {
        const timeDiff = now - lastClickPosition.current.time;
        const distance = Math.sqrt(
          Math.pow(e.clientX - lastClickPosition.current.x, 2) +
          Math.pow(e.clientY - lastClickPosition.current.y, 2)
        );
        
        if (timeDiff < rageClickThreshold && distance < rageClickDistance) {
          // console.log supprimé
        }
      }
      
      lastClickPosition.current = { x: e.clientX, y: e.clientY, time: now };
      
      // Garder seulement les 100 derniers clics
      if (clicks.current.length > 100) {
        clicks.current = clicks.current.slice(-100);
      }
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [firstClickTime]);

  // Tracking du scroll
  useEffect(() => {
    const handleScroll = () => {
      const now = Date.now();
      const currentScroll = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollDepth = (currentScroll / documentHeight) * 100;
      
      // Mettre à jour la profondeur maximale
      if (scrollDepth > maxScrollDepth.current) {
        maxScrollDepth.current = scrollDepth;
      }
      
      // Calculer la vitesse de scroll
      const timeDiff = now - lastScrollTime.current;
      const scrollDiff = Math.abs(currentScroll - lastScrollPosition.current);
      const scrollSpeed = timeDiff > 0 ? scrollDiff / timeDiff : 0;
      
      // Détecter la direction
      const direction = currentScroll > lastScrollPosition.current ? 'down' : 'up';
      
      scrollData.current.push({
        depth: scrollDepth,
        speed: scrollSpeed,
        direction,
        timestamp: now,
      });
      
      lastScrollPosition.current = currentScroll;
      lastScrollTime.current = now;
      
      // Garder seulement les 100 derniers événements de scroll
      if (scrollData.current.length > 100) {
        scrollData.current = scrollData.current.slice(-100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Tracking des hovers sur CTA
  const trackCTAHover = useCallback((elementId: string, isEntering: boolean) => {
    if (isEntering) {
      ctaHoverStart.current = { element: elementId, time: Date.now() };
    } else if (ctaHoverStart.current && ctaHoverStart.current.element === elementId) {
      const duration = Date.now() - ctaHoverStart.current.time;
      ctaHovers.current.push({
        element: elementId,
        duration,
        timestamp: Date.now(),
      });
      ctaHoverStart.current = null;
    }
  }, []);

  // Tracking des toggles
  const trackToggle = useCallback(() => {
    interactions.current.toggleClicks++;
  }, []);

  // Tracking des tooltips
  const trackTooltip = useCallback(() => {
    interactions.current.tooltipOpens++;
  }, []);

  // Tracking des FAQ
  const trackFAQ = useCallback((question: string) => {
    if (!interactions.current.faqOpens.includes(question)) {
      interactions.current.faqOpens.push(question);
    }
  }, []);

  // Tracking des comparaisons de plans
  const trackPlanComparison = useCallback(() => {
    interactions.current.planComparisons++;
  }, []);

  // Tracking des tentatives de copie
  useEffect(() => {
    const handleCopy = () => {
      interactions.current.textCopyAttempts++;
    };

    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, []);

  // Calcul des statistiques d'hésitation
  const calculateHesitationStats = useCallback(() => {
    const hesitationScores: number[] = [];
    for (let i = 10; i <= mousePositions.current.length; i++) {
      const subset = mousePositions.current.slice(i - 10, i);
      let directionChanges = 0;
      let totalDistance = 0;
      
      for (let j = 1; j < subset.length; j++) {
        const prev = subset[j - 1];
        const curr = subset[j];
        
        const dx = curr.x - prev.x;
        const dy = curr.y - prev.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        totalDistance += distance;
        
        if (j > 1) {
          const prevPrev = subset[j - 2];
          const prevDx = prev.x - prevPrev.x;
          const prevDy = prev.y - prevPrev.y;
          
          const dotProduct = dx * prevDx + dy * prevDy;
          if (dotProduct < 0) {
            directionChanges++;
          }
        }
      }
      
      if (totalDistance > 0) {
        hesitationScores.push((directionChanges / totalDistance) * 1000);
      }
    }
    
    return {
      averageHesitation: hesitationScores.length > 0 
        ? hesitationScores.reduce((a, b) => a + b, 0) / hesitationScores.length 
        : 0,
      maxHesitation: hesitationScores.length > 0 ? Math.max(...hesitationScores) : 0,
    };
  }, []);

  // Calcul des rage clicks
  const calculateRageClicks = useCallback(() => {
    let rageClickCount = 0;
    
    for (let i = 1; i < clicks.current.length; i++) {
      const prev = clicks.current[i - 1];
      const curr = clicks.current[i];
      
      const timeDiff = curr.timestamp - prev.timestamp;
      const distance = Math.sqrt(
        Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2)
      );
      
      if (timeDiff < rageClickThreshold && distance < rageClickDistance) {
        rageClickCount++;
      }
    }
    
    return rageClickCount;
  }, []);

  // Calcul des retours en arrière (scroll up)
  const calculateScrollBackwards = useCallback(() => {
    return scrollData.current.filter(s => s.direction === 'up').length;
  }, []);

  // Sauvegarder les données périodiquement et à la fermeture
  useEffect(() => {
    const saveTrackingData = async () => {
      // Permettre le tracking même pour les utilisateurs non connectés
      const hesitationStats = calculateHesitationStats();
      const rageClicks = calculateRageClicks();
      const scrollBackwards = calculateScrollBackwards();
      
      const trackingData = {
        sessionId: sessionIdRef.current,
        userId: userId || null,
        timestamp: new Date(),
        
        // Temps avant premier clic
        timeToFirstClick: firstClickTime,
        
        // Hésitations
        averageHesitation: hesitationStats.averageHesitation,
        maxHesitation: hesitationStats.maxHesitation,
        
        // Rage clicks
        rageClicksCount: rageClicks,
        
        // Dead clicks
        deadClicksCount: clicks.current.filter(c => c.isDeadClick).length,
        deadClicks: clicks.current.filter(c => c.isDeadClick).map(c => ({
          target: c.target,
          timestamp: c.timestamp,
          x: c.x,
          y: c.y,
        })),
        
        // Scroll
        maxScrollDepth: maxScrollDepth.current,
        averageScrollSpeed: scrollData.current.length > 0
          ? scrollData.current.reduce((sum, s) => sum + s.speed, 0) / scrollData.current.length
          : 0,
        scrollBackwardsCount: scrollBackwards,
        
        // Hovers sur CTA
        ctaHoversCount: ctaHovers.current.length,
        ctaHoversDurations: ctaHovers.current,
        
        // Interactions
        toggleClicksCount: interactions.current.toggleClicks,
        tooltipOpensCount: interactions.current.tooltipOpens,
        faqOpensCount: interactions.current.faqOpens.length,
        faqQuestions: interactions.current.faqOpens,
        planComparisonsCount: interactions.current.planComparisons,
        textCopyAttemptsCount: interactions.current.textCopyAttempts,
        
        // Statistiques générales
        totalClicks: clicks.current.length,
        totalMouseMoves: mousePositions.current.length,
        sessionDuration: Date.now() - pageLoadTime.current,
        
        // Tracking des cookies affiliés
        hasAffiliateCookie: !!affiliateCookieRef.current,
        affiliateId: affiliateCookieRef.current?.affiliateId,
        affiliateName: affiliateCookieRef.current?.affiliateName,
        affiliateCode: affiliateCookieRef.current?.code,
      };

      await pricingTrackingService.saveTrackingData(trackingData);
      
      // Log pour debug
      console.log('📊 Tracking sauvegardé:', {
        hasAffiliateCookie: trackingData.hasAffiliateCookie,
        affiliateId: trackingData.affiliateId,
        affiliateName: trackingData.affiliateName,
        affiliateCode: trackingData.affiliateCode,
        userId: trackingData.userId
      });
    };

    // Sauvegarder toutes les 30 secondes
    const interval = setInterval(saveTrackingData, 30000);

    // Sauvegarder avant de quitter la page
    const handleBeforeUnload = () => {
      saveTrackingData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      saveTrackingData(); // Sauvegarder une dernière fois
    };
  }, [
    userId,
    firstClickTime,
    calculateHesitationStats,
    calculateRageClicks,
    calculateScrollBackwards,
  ]);

  return {
    trackCTAHover,
    trackToggle,
    trackTooltip,
    trackFAQ,
    trackPlanComparison,
  };
};

// Fonction utilitaire pour décrire un élément
function getElementDescription(element: HTMLElement): string {
  const tag = element.tagName.toLowerCase();
  const id = element.id ? `#${element.id}` : '';
  // Sur les éléments SVG, `className` est un SVGAnimatedString (pas un string) : .split plante (A006)
  const rawClassName = typeof element.className === 'string'
    ? element.className
    : element.getAttribute('class') || '';
  const className = rawClassName ? `.${rawClassName.split(' ').join('.')}` : '';
  const text = element.textContent?.substring(0, 30) || '';
  
  return `${tag}${id}${className} - "${text}"`;
}

